import {
    GroupedProgressHeader,
    GroupedProgressLegend,
    GroupedProgressMeter,
    GroupedProgressRowHeader,
} from 'Components/molecules/GroupedProgress';
import type { GroupedProgressConfig, GroupedProgressSegmentConfig } from './groupedProgressConfig';
import './styles.scss';

/**
 * Represents a group of items for the progress meter (e.g. a department, role, organization, finding kind).
 */
export interface GroupedProgressItem {
    /** Unique id for the group (used for selection/filtering). */
    groupId: string;
    /** Display label for the group. */
    groupLabel: string;
    /** Count in "completed" state. */
    completed: number;
    /** Count in "in progress" state. */
    inProgress: number;
    /** Count in "outstanding" / not started state. */
    outstanding: number;
    /** Total count (completed + inProgress + outstanding). */
    total: number;
}

export interface GroupedProgressProps {
    /** Groups to display (e.g. by department, role, organization). */
    groups: GroupedProgressItem[];
    /** Title and segment labels/colors. */
    config: GroupedProgressConfig;
    /** Called when a group row is clicked; pass groupId to filter or drill down. */
    onGroupClick: (groupId: string) => void;
    /** Currently selected group id (e.g. for filtering). */
    selectedGroupId: string | null;
}

function getSegmentPercentages(group: GroupedProgressItem): Record<string, number> {
    const { total, completed, inProgress, outstanding } = group;
    if (total <= 0) {
        return { completed: 0, inProgress: 0, outstanding: 0 };
    }
    return {
        completed: Math.round((completed / total) * 100),
        inProgress: Math.round((inProgress / total) * 100),
        outstanding: Math.round((outstanding / total) * 100),
    };
}

function getSegmentCounts(group: GroupedProgressItem): Record<string, number> {
    return {
        completed: group.completed,
        inProgress: group.inProgress,
        outstanding: group.outstanding,
    };
}

function toMeterSegments(segments: GroupedProgressSegmentConfig[]): Array<{ key: string; label: string; color: string }> {
    return segments.map(s => ({ key: s.key, label: s.label, color: s.color }));
}

function toLegendItems(
    segments: GroupedProgressSegmentConfig[],
    counts: Record<string, number>
): Array<{ label: string; color: string; count: number }> {
    return segments.map(s => ({
        label: s.label,
        color: s.color,
        count: counts[s.key] ?? 0,
    }));
}

const GroupedProgressRoot = ({
    groups,
    config,
    onGroupClick,
    selectedGroupId,
}: GroupedProgressProps) => {
    if (!groups.length) {
        return null;
    }

    const meterSegments = toMeterSegments(config.segments);

    return (
        <div className="grouped-progress w-100">
            <GroupedProgressRoot.Header title={config.title} subtitle={config.subtitle} />
            <div className="grouped-progress__list">
                {groups.map((group) => {
                    const percentages = getSegmentPercentages(group);
                    const counts = getSegmentCounts(group);
                    const isSelected = selectedGroupId !== null && selectedGroupId === group.groupId;

                    return (
                        <div
                            key={group.groupId}
                            role="button"
                            tabIndex={0}
                            className={`grouped-progress__row ${isSelected ? 'grouped-progress__row--selected' : ''}`}
                            onClick={() => onGroupClick(group.groupId)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onGroupClick(group.groupId);
                                }
                            }}
                            aria-pressed={isSelected}
                            aria-label={`${group.groupLabel}: ${group.completed} of ${group.total} completed`}
                        >
                            <GroupedProgressRoot.RowHeader
                                groupLabel={group.groupLabel}
                                completedCount={group.completed}
                                totalCount={group.total}
                            />
                            <GroupedProgressRoot.Meter
                                segments={meterSegments}
                                percentages={percentages}
                                counts={counts}
                            />
                            <GroupedProgressRoot.Legend
                                items={toLegendItems(config.segments, counts)}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

GroupedProgressRoot.Header = GroupedProgressHeader;
GroupedProgressRoot.Meter = GroupedProgressMeter;
GroupedProgressRoot.Legend = GroupedProgressLegend;
GroupedProgressRoot.RowHeader = GroupedProgressRowHeader;

export const GroupedProgress = GroupedProgressRoot;
export default GroupedProgress;
